import { useRef } from "react";
import { styled } from "@pigment-css/react";
import { CalendarDay } from "./CalendarDay";
import { CALENDAR_DAY_WIDTH } from "./constants";
import { useDirectionalShift } from "./useDirectionalShift";
import type { CalendarDayModel } from "./types";

const Root = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
  width: "100%",
}));

const WeekdayHeader = styled("div")(({ theme }) => ({
  display: "grid",
  columnGap: theme.fieldSalesTwo.spacing[12],
  justifyContent: "center",
  justifyItems: "center",
  width: "100%",
}));

const Days = styled("div")(({ theme }) => ({
  display: "grid",
  columnGap: theme.fieldSalesTwo.spacing[12],
  rowGap: theme.fieldSalesTwo.spacing[16],
  justifyContent: "center",
  justifyItems: "center",
  width: "100%",
}));

const WeekdayCell = styled("span")(({ theme }) => ({
  textAlign: "center",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[13],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.wide,
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

interface CalendarGridProps {
  /** Column headers, e.g. ["Mo", "Di", …]. Length sets the column count (5 or 7). */
  weekdayLabels: string[];
  /** Week rows; each row's length must match `weekdayLabels`. */
  weeks: CalendarDayModel[][];
  onSelectDay: (date: Date) => void;
}

export function CalendarGrid({ weekdayLabels, weeks, onSelectDay }: CalendarGridProps) {
  const daysRef = useRef<HTMLDivElement>(null);
  const templateColumns = `repeat(${weekdayLabels.length}, minmax(0, ${CALENDAR_DAY_WIDTH}px))`;

  useDirectionalShift(daysRef, weeks[0]?.[0]?.date.getTime() ?? 0);

  return (
    <Root>
      <WeekdayHeader style={{ gridTemplateColumns: templateColumns }}>
        {weekdayLabels.map((label) => (
          <WeekdayCell key={label}>{label}</WeekdayCell>
        ))}
      </WeekdayHeader>
      <Days ref={daysRef} style={{ gridTemplateColumns: templateColumns }}>
        {weeks.flat().map((day) => (
          <CalendarDay
            key={day.date.toISOString()}
            label={day.label}
            ariaLabel={day.ariaLabel}
            state={day.state}
            hasMeeting={day.hasMeeting}
            isToday={day.isToday}
            onSelect={() => onSelectDay(day.date)}
          />
        ))}
      </Days>
    </Root>
  );
}
